import siteConfig from "site-config";
import { Observable } from "rxjs";
import { push } from "react-router-redux";
import { stringify } from "query-string";
import { ajax } from "lib/observable";
import { formatMessage } from "lib/globalize-util";
import "rxjs/add/operator/map";
import { APIError } from "lib/errors";
import {
    WHITEBOARD_BOARD_FETCH,
    WHITEBOARD_BOARD_FETCH_COMPLETED,
    WHITEBOARD_BOARDS_CLEAR,
    WHITEBOARD_BOARDS_CREATE,
    WHITEBOARD_BOARDS_DELETE,
    WHITEBOARD_BOARDS_FETCH,
    WHITEBOARD_EXPORT_PDF_ACKNOWLEDGE,
    WHITEBOARD_EXPORT_PDF_COMPLETED,
    WHITEBOARD_EXPORT_PDF_SUBMIT,
    WHITEBOARD_EXPORT_PDF_SUBMITTED,
    WHITEBOARD_PERMISSIONS_FETCH_COMPLETED,
    WHITEBOARD_PROPERTY_CHANGE,
    WORKING_ORDER_EXPORT_TO_WHITEBOARD,
    WORKING_ORDER_LINK_WHITEBOARD,
    WHITEBOARD_BOARDS_DUPLICATE,
} from "constants/action-types";
import { BoardDimensions } from "constants/whiteboard";
import { mapSetOperation, listCreate, mapCreate } from "actions/mutate";

import { add as snackbarAdd } from "actions/snackbars";
import { boards, pages, reset } from "actions/whiteboard";
import { Boards as BoardsSchema, BoardPermissions as PermissionsSchema } from "schemas/whiteboard";
import { getRecordsFromSchema, normalizeData } from "lib/api";
import { updateEntities } from "redux-query";
import me from "models/me";

import { BrandRecord as Brand } from "records/brand";
import { ITEM_CREATION_RETRIES } from "containers/folders/utils/constants";
const {
    createBoardError,
    createBoardSuccess,
    deleteBoardError,
    deleteBoardSuccess,
    fetchBoardsError,
    fetchBoardsSuccess,
} = boards;
const FETCH_WHITEBOARD_LIMIT = 10;

export const fetchBoardEpic = (action$, store) => {
    return action$
        .ofType(WHITEBOARD_BOARD_FETCH)
        .map((action) => action.payload)
        .concatMap(({ id, userId }) => {
            return ajax(`${siteConfig.goAPI}/v1/boards/${id}?with_snapshot=true`)
                .map(({ response }) => {
                    return { board: response.board };
                })
                .flatMap(({ board }) =>
                    ajax(`${siteConfig.goAPI}/v1/boards/${board.id}/permissions?min_identity=${userId}&limit=1`).map(
                        ({ response }) => {
                            return {
                                board,
                                permissionResponse: {
                                    data: [{ permissions: response.data }],
                                },
                                permissions: response.data,
                            };
                        },
                    ),
                )
                .catch((error) => {
                    return Observable.of({
                        error: true,
                        payload: new APIError(error.xhr.response.error, error.xhr.status, error.xhr.statusText),
                        type: WHITEBOARD_BOARD_FETCH_COMPLETED,
                    });
                });
        })
        .do((response) => {
            if (response.error) {
                throw response.payload;
            }
        })
        .map(({ board, permissions, permissionResponse }) => {
            const permissionRecords = getRecordsFromSchema(PermissionsSchema);
            const permissionData = normalizeData(permissionResponse, PermissionsSchema, permissionRecords);
            return {
                board,
                entities: {
                    permissions: permissionData.entities,
                },
                permissions,
            };
        })
        .flatMap(({ board, permissions, entities }) => {
            const { routing } = store.getState();
            const page = routing.locationBeforeTransitions.pathname.includes("/page")
                ? routing.locationBeforeTransitions.pathname.match(/\/page\/(.*)/)[1]
                : undefined;
            const actions = [
                updateEntities({
                    boardPermission: (boardPermission = {}) => {
                        return {
                            ...boardPermission,
                            ...entities.permissions.boardPermission,
                        };
                    },
                }),
                {
                    payload: board,
                    type: WHITEBOARD_BOARD_FETCH_COMPLETED,
                },
                {
                    meta: {
                        hasMore: false,
                    },
                    payload: permissions,
                    type: WHITEBOARD_PERMISSIONS_FETCH_COMPLETED,
                },
                mapCreate("nuorder.service.board", board.id, board.snapshot.root.mutable_map.fields, board.root_ref),
                listCreate(
                    "nuorder.service.board.pages",
                    board.snapshot.pages.ref,
                    board.snapshot.pages.mutable_list.values,
                ),
                pages.fetchById(page || board.snapshot.pages.mutable_list.values[0].ref.object_id),
            ];

            return actions;
        })
        .catch((error) => {
            return Observable.of({
                error: true,
                payload: error,
                type: WHITEBOARD_BOARD_FETCH_COMPLETED,
            });
        });
};

export const exportToPDFEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_EXPORT_PDF_SUBMIT)
        .map(({ payload: { boardId } }) => boardId)
        .switchMap((boardId) => {
            const { whiteboard } = store.getState();
            if (whiteboard.isProductAssetUploading) {
                return [
                    store.dispatch(
                        snackbarAdd({ message: formatMessage("Please wait until component assets upload") }),
                    ),
                ];
            } else {
                return ajax({
                    url: `${siteConfig.goAPI}/v1/boards/${boardId}/exports`,
                    method: "POST",
                    body: { type: "PDF" },
                })
                    .map(({ response, status }) => {
                        if (status >= 300 || !response.data || !response.data.length) {
                            throw new APIError("Error", status, "Error");
                        }

                        return {
                            payload: response.data[0],
                            type: WHITEBOARD_EXPORT_PDF_SUBMITTED,
                        };
                    })
                    .catch((error) => {
                        return Observable.of({
                            error: true,
                            payload: error,
                            type: WHITEBOARD_EXPORT_PDF_SUBMITTED,
                        });
                    });
            }
        });

export const duplicateBoardEpic = (action$, store) =>
    action$.ofType(WHITEBOARD_BOARDS_DUPLICATE).switchMap(({ payload: { boardId, boardName } }) => {
        const { scope } = store.getState();
        return ajax({
            url: `${siteConfig.goAPI}/v1/boards/${boardId}/copy`,
            method: "POST",
            body: { board_name: boardName },
        })
            .map(({ response }) => {
                if (!response) {
                    throw new Error("Bad response");
                } else {
                    const records = getRecordsFromSchema(BoardsSchema);
                    const data = normalizeData(response, BoardsSchema, records);
                    return { entities: data.entities, result: data.result };
                }
            })
            .mergeMap(({ entities, result }: any) => {
                return [
                    reset(),
                    updateEntities({
                        boards: (boards = {}) => {
                            return {
                                ...boards,
                                ...entities.boards,
                            };
                        },
                    }),
                    createBoardSuccess(result[0]),
                    push(`/brand/${scope.brand._id}/whiteboard/${result[0]}?edit=true`),
                    snackbarAdd({ message: formatMessage("Board duplicated") }),
                ];
            })
            .catch((error) => {
                return Observable.of(snackbarAdd({ message: formatMessage("Error duplicating board") }));
            });
    });

export const waitForPDFEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_EXPORT_PDF_SUBMITTED)
        .filter(({ error }) => !error)
        .map(({ payload }) => ({ pdfId: payload.id, boardId: payload.board_id }))
        .switchMap(({ pdfId, boardId }) => {
            return (
                ajax(`${siteConfig.goAPI}/v1/boards/${boardId}/exports/${pdfId}`)
                    .map(({ response, status }) => {
                        if (response.data && response.data.length && response.data[0].status) {
                            if (response.data[0].status === "CREATED") {
                                throw new APIError("generating", 202, "generating");
                            }
                            return {
                                error: response.data[0].status !== "FINISHED",
                                payload: response.data[0],
                                type: WHITEBOARD_EXPORT_PDF_COMPLETED,
                            };
                        }

                        return {
                            error: true,
                            payload: new APIError("PDF generating error", status, "PDF generating error"),
                            type: WHITEBOARD_EXPORT_PDF_COMPLETED,
                        };
                    })
                    .retryWhen((errors) => errors.delay(3000))
                    // Stop if the user dismisses
                    .takeUntil(action$.ofType(WHITEBOARD_EXPORT_PDF_ACKNOWLEDGE))
                    .catch((error) => {
                        return Observable.of({
                            error: true,
                            payload: error,
                            type: WHITEBOARD_EXPORT_PDF_COMPLETED,
                        });
                    })
            );
        });

export const fetchBoardsEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_BOARDS_FETCH)
        .map(({ payload }) => payload)
        // .debounceTime(1000)
        .switchMap(({ userId, options }) => {
            const queries = stringify({
                limit: FETCH_WHITEBOARD_LIMIT,
                max_granted_at_ms: undefined,
                min_board_id: undefined,
                role: undefined,
                sort_by_board_id_asc: undefined,
                ...options,
            });
            return ajax({
                url: `${siteConfig.goAPI}/v1/users/${userId}/boards?${queries}`,
                hideBeforeUnloadWarning: true,
            })
                .map(({ response }) => {
                    if (!response) {
                        return { result: [] };
                    } else {
                        response.data = response.data.filter(
                            (whiteboard) => whiteboard.permissions && whiteboard.permissions.length > 0,
                        );
                        const boardRecords = getRecordsFromSchema(BoardsSchema);
                        const boardData = normalizeData(response, BoardsSchema, boardRecords);
                        const permissionRecords = getRecordsFromSchema(PermissionsSchema);
                        const permissionData = normalizeData(response, PermissionsSchema, permissionRecords);

                        return {
                            entities: {
                                boards: boardData.entities,
                                permissions: permissionData.entities,
                            },
                            result: boardData.result,
                        };
                    }
                })
                .mergeMap(({ entities, result }: any) => [
                    updateEntities({
                        boardPermission: (boardPermission = {}) => {
                            return {
                                ...boardPermission,
                                ...entities.permissions.boardPermission,
                            };
                        },
                        boards: (boards = {}) => {
                            return {
                                ...boards,
                                ...entities.boards.boards,
                            };
                        },
                    }),
                    fetchBoardsSuccess(result),
                ])
                .takeUntil(action$.ofType(WHITEBOARD_BOARDS_CLEAR)) // route navigation cancellation
                .catch((error) =>
                    Observable.merge(
                        Observable.of(fetchBoardsError(error, true)),
                        Observable.of(
                            snackbarAdd({ message: formatMessage("There was an error fetching your boards") }),
                        ),
                    ),
                );
        });

const createBoardAjaxRequest = ({
    request,
    forOrder,
    orderId,
    retry,
    callback,
}: {
    request: any;
    forOrder: boolean;
    orderId: string;
    retry?: number;
    callback?: (whiteboardId: string, failureCount?: number) => void;
}) => {
    return ajax(request)
        .map(({ response }) => {
            if (!response) {
                return { result: [] };
            } else {
                const records = getRecordsFromSchema(BoardsSchema);
                const data = normalizeData(response, BoardsSchema, records);
                return { entities: data.entities, result: data.result };
            }
        })
        .mergeMap(({ entities, result }: any) => {
            const actions = [
                updateEntities({
                    boards: (boards = {}) => {
                        return {
                            ...boards,
                            ...entities.boards,
                        };
                    },
                }),
                createBoardSuccess(result[0]),
            ];

            if (forOrder) {
                actions.push({
                    payload: {
                        orderId,
                        whiteboardId: result[0],
                    },
                    type: WORKING_ORDER_LINK_WHITEBOARD,
                });
            } else if (callback) {
                callback(result[0]);
            } else {
                window.location.href = `/b/whiteboard/${result[0]}`;
            }

            return actions;
        })
        .catch((error) => {
            const alreadyExistsError = error.xhr?.response?.code === 6;
            const retryCount = (retry || 0) + 1;
            if (callback && alreadyExistsError && retryCount < ITEM_CREATION_RETRIES) {
                callback(null, retry || 0);
                return Observable.empty();
            } else {
                return Observable.merge(
                    Observable.of(createBoardError(error, true)),
                    Observable.of(snackbarAdd({ message: formatMessage("There was an error creating your board") })),
                );
            }
        });
};

export const createBoardEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_BOARDS_CREATE)
        .map(({ payload }) => payload)
        .concatMap(({ name, forOrder, orderId, merchant }) => {
            const { scope, routing } = store.getState();
            const brand_name = scope.brand?.name;
            const brand_id = scope.brand?._id;
            const merchantId = routing.locationBeforeTransitions.query.merchantId || merchant?.merchantId;

            const request = {
                body: {
                    board_name: name,
                    brand_id,
                    brand_name,
                    merchant_id: merchantId,
                    parent: merchant?.parentId,
                    page_size: {
                        height: BoardDimensions.LETTER.height,
                        width: BoardDimensions.LETTER.width,
                    },
                    meta: {
                        version: "2",
                        has_versioned_products: scope.brand?.hasPreference("has_versioned_products") ? "true" : "false",
                    },
                },
                method: "POST",
                url: `${siteConfig.goAPI}/v1/boards`,
            };

            if (merchantId) {
                me.setLastMerchantId(merchantId);
                return ajax(`${siteConfig.goAPI}/v1/merchants/${merchantId}/brand_portals`)
                    .map(({ response }) => {
                        const brandId = response.brand_portals?.[0]?.brand_portal_id;
                        if (!brandId) {
                            throw new Error(formatMessage("Merchant not linked to any brands"));
                        }
                        return { brandId };
                    })
                    .flatMap(({ brandId }) => {
                        return ajax(`${siteConfig.api}/brand/${brandId}`)
                            .map(({ response }) => ({ brand: new Brand(response) }))
                            .flatMap(({ brand }) => {
                                request.body.brand_name = brand.name;
                                request.body.brand_id = brand._id;
                                request.body.merchant_id = merchantId;
                                request.body.meta.has_versioned_products = brand?.hasPreference(
                                    "has_versioned_products",
                                )
                                    ? "true"
                                    : "false";
                                return createBoardAjaxRequest({
                                    request,
                                    forOrder,
                                    orderId,
                                    retry: merchant?.retry,
                                    callback: merchant?.callback,
                                });
                            });
                    })
                    .catch((error) => {
                        return Observable.of(
                            snackbarAdd({
                                message: error.message || formatMessage("There was an error creating the whiteboard"),
                            }),
                        );
                    });
            } else {
                return createBoardAjaxRequest({
                    request,
                    forOrder,
                    orderId,
                    retry: merchant?.retry,
                    callback: merchant?.callback,
                });
            }
        });

export const createBoardFromOrder = (action$, store) =>
    action$.ofType(WORKING_ORDER_EXPORT_TO_WHITEBOARD).mergeMap(({ payload }) => {
        if (payload.whiteboardId) {
            // const { scope } = store.getState();
            // return Observable.of(push(`/b/whiteboard/${payload.whiteboardId}?filterByWorkingOrder=true`));
            window.location.href = `/b/whiteboard/${payload.whiteboardId}?filterByWorkingOrder=true`;
        }
        return Observable.of({
            type: WHITEBOARD_BOARDS_CREATE,
            payload: {
                name: `${formatMessage("Order Number")}: ${payload.orderNumber}`,
                forOrder: true,
                orderId: payload.orderId,
            },
        });
    });

export const linkBoardtoWorkingOrderEpic = (action$, store) =>
    action$.ofType(WORKING_ORDER_LINK_WHITEBOARD).mergeMap(({ payload }) => {
        const request = {
            body: {
                __whiteboard_id: payload.whiteboardId,
            },
            method: "POST",
            url: `${siteConfig.api}/working/update/${payload.orderId}`,
        };

        return ajax(request)
            .mergeMap(() => {
                window.location.href = `/b/whiteboard/${payload.whiteboardId}?filterByWorkingOrder=true`;
                return [];
            })
            .catch((error) =>
                Observable.of(snackbarAdd({ message: formatMessage("There was an error updating the working order") })),
            );
    });

export const deleteBoardEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_BOARDS_DELETE)
        .map(({ payload }) => payload)
        .mergeMap(({ boardId }) =>
            ajax({ url: `${siteConfig.goAPI}/v1/boards/${boardId}`, method: "DELETE" })
                .mergeMap(() => {
                    const { routing, scope } = store.getState();

                    return routing.locationBeforeTransitions.pathname.includes("/whiteboard/")
                        ? [
                              deleteBoardSuccess(boardId),
                              push(`/brand/${scope.brand._id}/whiteboards`),
                              store.dispatch(snackbarAdd({ message: "Board successfully deleted" })),
                          ]
                        : [
                              deleteBoardSuccess(boardId),
                              store.dispatch(snackbarAdd({ message: "Board successfully deleted" })),
                          ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of(deleteBoardError(error, true)),
                        Observable.of(
                            snackbarAdd({ message: formatMessage("There was an error deleting your board") }),
                        ),
                    ),
                ),
        );

export const updateBoardPropertyEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_PROPERTY_CHANGE)
        .map(({ payload }) => {
            const { whiteboard, mutate } = store.getState();
            const boardAttribute = mutate.snapshots.get(whiteboard.boardId).get(payload.key);
            return { whiteboard, payload, boardAttribute };
        })
        .debounceTime(1000)
        .map(({ whiteboard, payload, boardAttribute }) => {
            const { key, value } = payload;
            return mapSetOperation(whiteboard.boardId, key, boardAttribute.mergeDeep(value));
        });
